import React from 'react';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    LinearProgress,
    Chip,
    Avatar,
    Stack,
    Paper
} from '@mui/material';
import {
    TrendingUp,
    TrendingDown,
    People,
    Category,
    EmojiEvents,
    Assessment,
    Security,
    Speed,
    Timeline,
    ShowChart
} from '@mui/icons-material';

const EnhancedStatsGrid = ({ stats, loading }) => {
    const calculateTrend = (current, previous) => {
        if (!previous || previous === 0) return { direction: 'up', value: '0%', color: '#4caf50' };
        const change = ((current - previous) / previous) * 100;
        return {
            direction: change >= 0 ? 'up' : 'down',
            value: `${Math.abs(change).toFixed(1)}%`,
            color: change >= 0 ? '#4caf50' : '#f44336'
        };
    };

    const statsConfig = [
        {
            title: 'Total Users',
            value: stats?.totalUsers || 0,
            subtitle: `${stats?.activeUsers || 0} active, ${stats?.currentOnlineUsers || 0} online`,
            icon: <People />,
            color: '#2196f3',
            bgColor: '#ffffff',
            borderColor: '#e3f2fd',
            trend: calculateTrend(stats?.totalUsers, Math.max(1, (stats?.totalUsers || 1) - (stats?.weeklyNewUsers || 0))),
            progress: stats?.totalUsers > 0 ? Math.min(100, (stats?.activeUsers / stats?.totalUsers) * 100) : 0,
            progressLabel: 'User Activity Rate',
            route: '/admin/users'
        },
        {
            title: 'All User Classifications',
            value: stats?.totalClassifications || 0,
            subtitle: `${stats?.todayClassifications || 0} today, ${stats?.weeklyClassifications || 0} this week`,
            icon: <Category />,
            color: '#43a047',
            bgColor: '#ffffff',
            borderColor: '#e8f5e8',
            trend: calculateTrend(stats?.todayClassifications || 0, Math.max(1, (stats?.todayClassifications || 1) - 5)),
            progress: Math.min(100, (stats?.todayClassifications || 0) * 2),
            progressLabel: 'Daily Target Progress',
            route: '/admin/stats'
        },
        {
            title: 'Active Badges',
            value: stats?.activeBadges || 0,
            subtitle: `${stats?.totalBadges || 0} total badges`,
            icon: <EmojiEvents />,
            color: '#ff5722',
            bgColor: '#ffffff',
            borderColor: '#fbe9e7',
            trend: calculateTrend(stats?.activeBadges, Math.max(1, (stats?.activeBadges || 1) - 1)),
            progress: stats?.badgeUtilization || 0,
            progressLabel: 'Badge Utilization',
            route: '/admin/badges'
        },
        {
            title: 'AI Accuracy',
            value: `${stats?.avgAccuracy || 87}%`,
            subtitle: 'Classification precision',
            icon: <Security />,
            color: '#9c27b0',
            bgColor: '#ffffff',
            borderColor: '#f3e5f5',
            trend: calculateTrend(stats?.avgAccuracy || 87, 85),
            progress: stats?.avgAccuracy || 87,
            progressLabel: 'Performance Score',
            route: '/admin/stats'
        },
        {
            title: 'Weekly Growth',
            value: `+${stats?.weeklyGrowth || 0}%`,
            subtitle: `${stats?.weeklyNewUsers || 0} new users`,
            icon: <TrendingUp />,
            color: '#ff9800',
            bgColor: '#ffffff',
            borderColor: '#fff8e1',
            trend: { direction: 'up', value: `+${stats?.weeklyGrowth || 0}%`, color: '#4caf50' },
            progress: Math.min(100, (stats?.weeklyGrowth || 0) * 5),
            progressLabel: 'Growth Momentum',
            route: '/admin/stats'
        },
        {
            title: 'Monthly Growth',
            value: `+${stats?.monthlyGrowth || 0}%`,
            subtitle: `${stats?.weeklyNewUsers * 4 || 0} projected monthly`,
            icon: <Timeline />,
            color: '#607d8b',
            bgColor: '#ffffff',
            borderColor: '#eceff1',
            trend: { direction: 'up', value: `+${stats?.monthlyGrowth || 0}%`, color: '#4caf50' },
            progress: Math.min(100, (stats?.monthlyGrowth || 0) * 3),
            progressLabel: 'Monthly Trajectory',
            route: '/admin/stats'
        },
        {
            title: 'System Health',
            value: `${stats?.systemUptime || 99.0}%`,
            subtitle: `${stats?.responseTime || 1.5}s response time`,
            icon: <Speed />,
            color: '#00bcd4',
            bgColor: '#ffffff',
            borderColor: '#e0f2f1',
            trend: {
                direction: (stats?.errorRate || 2) < 5 ? 'up' : 'down',
                value: `${stats?.successRate || 98}%`,
                color: (stats?.errorRate || 2) < 5 ? '#4caf50' : '#f44336'
            },
            progress: stats?.systemUptime || 99.0,
            progressLabel: 'System Uptime',
            route: '/admin/stats'
        },
        {
            title: 'Top Category',
            value: stats?.topCategories?.[0]?.name || 'N/A',
            subtitle: `${stats?.topCategories?.[0]?.count || 0} classifications`,
            icon: <ShowChart />,
            color: '#795548',
            bgColor: '#ffffff',
            borderColor: '#efebe9',
            trend: {
                direction: 'up',
                value: `${stats?.topCategories?.[0]?.percentage || 0}%`,
                color: '#4caf50'
            },
            progress: stats?.topCategories?.[0]?.percentage || 0,
            progressLabel: 'Category Dominance',
            route: '/admin/categories'
        }
    ];

    return (
        <Grid container spacing={3}>
            {statsConfig.map((stat, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                    <Card
                        elevation={2}
                        sx={{
                            borderRadius: 3,
                            background: stat.bgColor,
                            border: `2px solid ${stat.borderColor}`,
                            position: 'relative',
                            overflow: 'hidden',
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 28px ${stat.color}25`,
                                border: `2px solid ${stat.color}50`
                            }
                        }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            {/* Header with Icon and Trend */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                <Paper
                                    elevation={1}
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        background: `linear-gradient(135deg, ${stat.color}15, ${stat.color}25)`,
                                        border: `1px solid ${stat.color}30`
                                    }}
                                >
                                    {React.cloneElement(stat.icon, {
                                        sx: { fontSize: 32, color: stat.color }
                                    })}
                                </Paper>

                                <Chip
                                    icon={stat.trend.direction === 'up' ?
                                        <TrendingUp sx={{ fontSize: '14px !important' }} /> :
                                        <TrendingDown sx={{ fontSize: '14px !important' }} />
                                    }
                                    label={stat.trend.value}
                                    size="small"
                                    sx={{
                                        background: `${stat.trend.color}15`,
                                        color: stat.trend.color,
                                        fontWeight: 700,
                                        border: `1px solid ${stat.trend.color}30`
                                    }}
                                />
                            </Box>

                            {/* Main Value */}
                            <Typography variant="h3" fontWeight={900} sx={{
                                color: stat.color,
                                mb: 0.5,
                                lineHeight: 1
                            }}>
                                {loading ? '...' : stat.value}
                            </Typography>

                            {/* Title */}
                            <Typography variant="h6" sx={{
                                color: '#333333',
                                fontWeight: 700,
                                mb: 1
                            }}>
                                {stat.title}
                            </Typography>

                            {/* Subtitle */}
                            <Typography variant="body2" sx={{
                                color: '#666666',
                                mb: 2,
                                minHeight: '2.5em',
                                lineHeight: 1.4
                            }}>
                                {stat.subtitle}
                            </Typography>

                            {/* Progress Bar with Clear Label */}
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="caption" sx={{
                                        color: '#555555',
                                        fontWeight: 600,
                                        fontSize: '0.75rem'
                                    }}>
                                        {stat.progressLabel}
                                    </Typography>
                                    <Typography variant="caption" sx={{
                                        color: stat.color,
                                        fontWeight: 700,
                                        fontSize: '0.75rem'
                                    }}>
                                        {Math.round(stat.progress)}%
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={loading ? 0 : Math.min(100, stat.progress)}
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        backgroundColor: `${stat.color}10`,
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 4,
                                            backgroundColor: stat.color,
                                            background: `linear-gradient(90deg, ${stat.color}, ${stat.color}cc)`
                                        }
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );
};

export default EnhancedStatsGrid;
